'use client'

import { Event } from '@/types'
import { ChallengeTheme, Statement } from '@/types/Event'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { EligibilityCriteriaComponent } from './EligibilityCriteria'
import { get, includes } from 'lodash'
import Link from 'next/link'
import {
  RichContentSection,
  HtmlContentSection,
} from '@/components/event-detail'

const sidebarList = [
  { section: 'overview', label: 'About' },
  { section: 'eligibility_criteria', label: 'Eligibility Criteria' },
  { section: 'challenge_themes', label: 'Challenge Themes' },
  { section: 'timeline', label: 'Timeline' },
  { section: 'submission_artefacts', label: 'Submission Requirements' },
  { section: 'judging_criteria', label: 'Judging Criteria' },
  { section: 'prizes', label: 'Prizes' },
  { section: 'custom_field', label: 'Custom Field' },
  { section: 'links', label: 'Links' },
  { section: 'sponsors', label: 'Sponsors' },
]

// This is result of empty content getting from squill editor
const EMPTY_CONTENT = '<p><br></p>'

const CONTENT_SECTIONS = [
  'overview',
  'timeline',
  'submission_artefacts',
  'judging_criteria',
  'prizes',
  'custom_field',
]

// A statement with a blank title renders as a bare numbered bullet, so it has
// always been filtered out of the list — which means a theme can carry
// statements and still have nothing worth displaying. Everything about
// statements keys off this rather than off `statements.length`, so a theme
// holding only blanks is treated the same as one holding none.
const visibleStatements = (theme: ChallengeTheme): Statement[] =>
  theme.statements?.filter((statement) => statement.title?.trim()) ?? []

// Helper function to convert title to slug format
const titleToSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^\w_]/g, '') // Remove special characters except underscores
}

const EventDetail = ({ event }: { event: Event }) => {
  const [activeSection, setActiveSection] = useState('')
  const [isScrolling, setIsScrolling] = useState(false)

  // Generate dynamic section ID for custom field
  const customFieldSectionId = event.contents?.custom_field?.title
    ? titleToSlug(event.contents.custom_field.title)
    : 'custom_field'

  // An admin who opens the custom field and saves without filling it in leaves
  // the object behind with blank strings, so its presence says nothing about
  // whether there is anything to show. Quill also writes EMPTY_CONTENT for an
  // editor that was cleared.
  const customFieldTitle = event.contents?.custom_field?.title?.trim() || ''
  const customFieldValue =
    event.contents?.custom_field?.value?.trim() &&
    event.contents.custom_field.value !== EMPTY_CONTENT
      ? event.contents.custom_field.value
      : ''

  // `challenge_statement` is the admin's intent — whether a submission must
  // cite a statement — not a promise that any were written. An event set to
  // require or allow them but left without any still rendered a "Challenge
  // Statements" heading over an empty list, so the content has to be there too.
  const showStatements =
    event.challenge_statement !== 'no_statement' &&
    event.challenge_themes.some((theme) => visibleStatements(theme).length > 0)

  useEffect(() => {
    const handleScroll = () => {
      // Don't update URL during programmatic scrolling
      if (isScrolling) return

      const sections = document.querySelectorAll('section')
      const scrollPosition = window.scrollY

      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 200
        const sectionHeight = section.clientHeight

        if (
          scrollPosition >= sectionTop &&
          scrollPosition < sectionTop + sectionHeight
        ) {
          const sectionId = section.id
          setActiveSection(sectionId)

          // Only update URL hash during natural scrolling (not programmatic)
          if (sectionId && window.location.hash !== `#${sectionId}`) {
            const url = new URL(window.location.href)
            url.hash = sectionId
            window.history.replaceState({}, '', url.toString())
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isScrolling])

  // Handle initial page load with hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const section = document.getElementById(hash)
        if (section) {
          setActiveSection(hash)

          // Set scrolling flag to prevent URL updates during initial scroll
          setIsScrolling(true)

          section.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })

          // Adjust for sticky header
          setTimeout(() => {
            window.scrollTo({
              top: section.offsetTop - 200,
              behavior: 'smooth',
            })

            // Reset scrolling flag after animation
            setTimeout(() => {
              setIsScrolling(false)
            }, 1000)
          }, 100)
        }
      }, 100)
    }
  }, [])

  const hasLink = () => !!event.term_of_service_url || !!event.event_rule_url

  const handleListItemClick = (sectionId: string) => {
    const section = document.getElementById(sectionId)
    if (section) {
      // Update URL hash immediately when user clicks
      const url = new URL(window.location.href)
      url.hash = sectionId
      window.history.pushState({}, '', url.toString())

      // Update active section state
      setActiveSection(sectionId)

      // Set scrolling flag to prevent URL updates during scroll animation
      setIsScrolling(true)

      // Scroll to section
      window.scrollTo({
        top: section.offsetTop - 200,
        behavior: 'smooth',
      })

      // Reset scrolling flag after animation completes
      setTimeout(() => {
        setIsScrolling(false)
      }, 1000) // Smooth scroll typically takes ~500-800ms, so 1000ms is safe
    }
  }

  const showContentIfPresent = (section: string) => {
    const content = get(event.contents, section)

    if (includes(CONTENT_SECTIONS, section)) {
      return content?.trim() && content !== EMPTY_CONTENT
    }
    if (section === 'sponsors') {
      return event.sponsors && event.sponsors.length > 0
    }
    if (section === 'links') {
      return hasLink()
    }
    return true
  }

  return (
    <section className="flex">
      <div className="hidden lg:block self-start sticky top-56 mt-8 w-1/5">
        <h6 className="text-xs font-semibold text-gray-400">SECTIONS</h6>
        <div className="flex flex-col text-sm pt-6 gap-4">
          {sidebarList.map((item, index) =>
            item.section === 'custom_field'
              ? customFieldTitle && (
                  <div key={`${item.section}_${index}`}>
                    <span
                      className={`text-gray-500 hover:text-gray-900 cursor-pointer ${
                        activeSection === customFieldSectionId && 'font-bold'
                      }`}
                      onClick={() => handleListItemClick(customFieldSectionId)}
                    >
                      {customFieldTitle}
                    </span>
                  </div>
                )
              : showContentIfPresent(item.section) && (
                  <div key={`${item.section}_${index}`}>
                    <span
                      className={`text-gray-500 hover:text-gray-900 cursor-pointer ${
                        activeSection === item.section && 'font-bold'
                      }`}
                      onClick={() => handleListItemClick(item.section)}
                    >
                      {item.label}
                    </span>
                  </div>
                )
          )}
        </div>
      </div>

      <div className="mt-8 w-full lg:w-4/5">
        <div className="flex flex-col divide-y ">
          {showContentIfPresent('overview') && (
            <HtmlContentSection
              id="overview"
              title="About"
              html={event.contents?.overview ?? ''}
              className="pb-14 pt-0"
            />
          )}
          {event.eligibility && (
            <EligibilityCriteriaComponent eligibility={event.eligibility} />
          )}
          <RichContentSection
            id="challenge_themes"
            title={
              event.challenge_themes.length > 1
                ? 'Challenge Themes'
                : 'Challenge Theme'
            }
          >
            {/* Both blurbs describe statements the reader is about to see, so
                they are held back until at least one theme actually has some.
                An event with none used to announce them and then show nothing
                but empty boxes. */}
            {showStatements &&
              event.challenge_statement === 'require_statement' && (
                <p className="text-gray-700">
                  Each theme has its own challenge statements, and picking one
                  is required for submission. So choose your favourite, and
                  let&#39;s get innovating!
                </p>
              )}
            {showStatements &&
              event.challenge_statement === 'ignore_statement' && (
                <p className="text-gray-700">
                  Each theme has its own challenge statements, but those are
                  optional sparks of inspiration – have fun exploring your own
                  ideas within the theme!
                </p>
              )}
            {event.challenge_themes.map((theme, index) => {
              const statements = visibleStatements(theme)

              return (
                <div
                  key={theme.id}
                  className="w-full flex flex-col text-gray-700"
                >
                  <h2 className="text-xl font-semibold">
                    Theme
                    {event.challenge_themes.length > 1 &&
                      ' ' + (index + 1)}: {theme.theme}
                  </h2>
                  <p>{theme.description}</p>
                  {/* Per theme, not per event: with several themes, only the
                      ones that actually have statements get a box. */}
                  {showStatements && statements.length > 0 && (
                    <div className="w-full mt-4 p-6 border border-gray-200 rounded-xl">
                      <h3 className="text-lg font-semibold">
                        Challenge Statements
                      </h3>
                      <ol className="list list-decimal flex flex-col mt-4 ml-4 gap-4">
                        {statements.map((statement, statementIndex) => (
                          <li key={`statement_${theme.id}_${statementIndex}`}>
                            {statement.title}
                            <p className="text-sm text-gray-600">
                              {statement.description}
                            </p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )
            })}
          </RichContentSection>
          {showContentIfPresent('timeline') && (
            <HtmlContentSection
              id="timeline"
              title="Timeline"
              html={event.contents?.timeline ?? ''}
            />
          )}
          {showContentIfPresent('submission_artefacts') && (
            <HtmlContentSection
              id="submission_artefacts"
              title="Submissions Requirements"
              html={event.contents?.submission_artefacts ?? ''}
            />
          )}
          {showContentIfPresent('judging_criteria') && (
            <HtmlContentSection
              id="judging_criteria"
              title="Judging Criteria"
              html={event.contents?.judging_criteria ?? ''}
            />
          )}
          {showContentIfPresent('prizes') && (
            <HtmlContentSection
              id="prizes"
              title="Prizes"
              html={event.contents?.prizes ?? ''}
            />
          )}
          {(customFieldTitle || customFieldValue) &&
            (customFieldValue ? (
              <HtmlContentSection
                id={customFieldSectionId}
                title={customFieldTitle}
                html={customFieldValue}
              />
            ) : (
              <RichContentSection
                id={customFieldSectionId}
                title={customFieldTitle}
              >
                <></>
              </RichContentSection>
            ))}

          {showContentIfPresent('links') && (
            <RichContentSection id="links" title="Links">
              <div className="w-full flex flex-col">
                {event.term_of_service_url && (
                  <>
                    <h3 className="text-gray-800 text-md font-semibold">
                      Hackathon event terms and service URL
                    </h3>
                    <div className="mb-8 mt-4 pl-3">
                      <Link
                        href={event.term_of_service_url}
                        className="text-blue-600 hover:underline break-all"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {event.term_of_service_url}
                      </Link>
                    </div>
                  </>
                )}

                {event.event_rule_url && (
                  <>
                    <h3 className="text-gray-800 text-md font-semibold">
                      Hackathon event Rules URL
                    </h3>
                    <div className="mb-8 mt-4 pl-3">
                      <Link
                        href={event.event_rule_url}
                        className="text-blue-600 hover:underline break-all"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {event.event_rule_url}
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </RichContentSection>
          )}

          {showContentIfPresent('sponsors') && (
            <RichContentSection id="sponsors" title="Our Sponsors">
              <div className="w-full flex flex-col gap-10">
                {event.sponsors?.map((sponsor, index) => (
                  <div
                    key={index}
                    className="w-full rounded-xl border p-10 flex flex-col items-center gap-10"
                  >
                    <h3>{sponsor.title.toUpperCase()}</h3>
                    <div className="flex flex-wrap items-center justify-center gap-14">
                      {sponsor.logos.map((logo, index) => (
                        <Image
                          key={`logo-${index}`}
                          className="hover:cursor-pointer"
                          src={logo}
                          alt=""
                          width="148"
                          height="40"
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </RichContentSection>
          )}
        </div>
      </div>
    </section>
  )
}

export default EventDetail
